import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Avatar,
  Stack,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import { styled } from "@mui/system";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';

// --- STYLED COMPONENTS ---

const ReviewCard = styled(Paper)(({ theme }) => ({
  padding: "16px",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)", // Ombra un po' più marcata per evidenziare che richiedono azione
  backgroundColor: "#fff",
  marginBottom: "16px",
  borderLeft: "6px solid #ff9f43", // Bordo arancione per indicare "Pending"
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)"
  }
}));

const HeaderRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "12px"
});

const ActionsRow = styled(Box)({
  display: "flex",
  gap: "8px",
  marginTop: "12px",
  paddingTop: "12px",
  borderTop: "1px solid #f1f2f6"
});

export default function CertificateReviewList({ 
    certificates = [], 
    users = [], 
    onApprove, 
    onRefuse 
}) {

    const [processingId, setProcessingId] = useState(null);

    const getUser = (userId) => users.find(u => u.id === userId);
    
    const getInitials = (name) => {
        if (!name) return "?";
        return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
    };

    const handleApprove = async (certId) => {
        if (!onApprove) return;
        setProcessingId(certId);
        try { await onApprove(certId); } catch (error) { alert("Errore approvazione"); }
        setProcessingId(null);
    };

    const handleRefuse = async (certId) => {
        if (!onRefuse) return;
        const note = window.prompt("Motivo del rifiuto (obbligatorio):");
        if (note === null) return;
        if (!note.trim()) return alert("Devi inserire un motivo.");
        
        setProcessingId(certId);
        try { await onRefuse(certId, note); } catch (error) { alert("Errore rifiuto"); }
        setProcessingId(null);
    };

    if (!certificates || certificates.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4, opacity: 0.6 }}>
                <AssignmentLateIcon sx={{ fontSize: 40, mb: 1, color: '#bdc3c7' }} />
                <Typography variant="body2" color="text.secondary">
                    Nessun certificato da revisionare.
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={0}>
            {certificates.map((cert) => {
                const user = getUser(cert.userId);
                const userName = user ? user.fullName : 'Utente Sconosciuto';
                const expiryDate = cert.expiresAt?.toDate 
                    ? cert.expiresAt.toDate().toLocaleDateString() 
                    : 'Data N/D';
                
                const isProcessing = processingId === cert.id;

                return (
                    <ReviewCard key={cert.id} elevation={0}>
                        
                        {/* HEADER: Avatar + Nome + Link File */}
                        <HeaderRow>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <Avatar sx={{ bgcolor: '#fff3cd', color: '#856404', fontWeight: 'bold' }}>
                                    {getInitials(userName)}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                                        {userName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Scadenza: {expiryDate}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Tooltip title="Vedi Certificato">
                                <IconButton 
                                    size="small" 
                                    href={cert.certificateUrl} 
                                    target="_blank"
                                    sx={{ bgcolor: '#f1f2f6' }}
                                >
                                    <VisibilityIcon fontSize="small" color="action" />
                                </IconButton>
                            </Tooltip>
                        </HeaderRow>

                        {/* ACTIONS: Approva / Rifiuta */}
                        <ActionsRow>
                            {isProcessing ? (
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={24} color="inherit" />
                                </Box>
                            ) : (
                                <>
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        color="success" 
                                        size="small"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() => handleApprove(cert.id)}
                                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold', boxShadow: 'none' }}
                                    >
                                        Approva
                                    </Button>
                                    <Button 
                                        fullWidth 
                                        variant="outlined" 
                                        color="error" 
                                        size="small"
                                        startIcon={<CancelIcon />}
                                        onClick={() => handleRefuse(cert.id)}
                                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold', borderWidth: '2px', '&:hover': { borderWidth: '2px' } }}
                                    >
                                        Rifiuta
                                    </Button>
                                </>
                            )}
                        </ActionsRow>

                    </ReviewCard>
                );
            })}
        </Stack>
    );
}